import React, { useState, useEffect } from 'react';
import { usersAPI, tenantAPI, deviceAPI, assignmentAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DeviceManagers = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isDeviceManager = user?.role === 'device_manager';
  const canManageUsers = isSuperAdmin || isDeviceManager;
  
  const [list, setList] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [devices, setDevices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [addEmail, setAddEmail] = useState('');
  const [addTenantId, setAddTenantId] = useState('');
  const [addRole, setAddRole] = useState('device_manager');
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'statistics'
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  
  // Edit state
  const [editingUserId, setEditingUserId] = useState(null);
  const [editDeliveryUnit, setEditDeliveryUnit] = useState('');
  const [editRole, setEditRole] = useState('');
  const [updating, setUpdating] = useState(false);

  // Import/Export state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Email sending state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailMode, setEmailMode] = useState('deliveryUnit'); // 'deliveryUnit' or 'specific'
  const [selectedDeliveryUnit, setSelectedDeliveryUnit] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]); // Array of user emails
  const [userSearchTerm, setUserSearchTerm] = useState(''); // Search filter for user list
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResults, setEmailResults] = useState(null);

  const loadDeviceManagers = async () => {
    try {
      const res = await usersAPI.getDeviceManagers();
      setList(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to load device managers' });
    }
  };

  const loadDevicesAndAssignments = async () => {
    try {
      const [devicesRes, assignmentsRes] = await Promise.all([
        deviceAPI.getAll(),
        assignmentAPI.getAll()
      ]);
      setDevices(devicesRes.data);
      setAssignments(assignmentsRes.data.filter(a => a.status === 'active'));
    } catch (err) {
      console.error('Failed to load devices/assignments:', err);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await tenantAPI.getAll();
      setTenants(res.data || []);
      if (res.data?.length && !addTenantId) {
        setAddTenantId(res.data[0].tenantId);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadDeviceManagers(), loadTenants(), loadDevicesAndAssignments()]);
      setLoading(false);
    })();
  }, []);

  // Debug: Monitor selectedUsers changes
  useEffect(() => {
    console.log('🔍 selectedUsers changed:', selectedUsers.length, 'users selected');
    if (selectedUsers.length > 0 && selectedUsers.length <= 5) {
      console.log('   Selected emails:', selectedUsers);
    } else if (selectedUsers.length > 5) {
      console.log('   Selected emails (first 5):', selectedUsers.slice(0, 5), '... +', selectedUsers.length - 5, 'more');
    }
  }, [selectedUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = addEmail.trim().toLowerCase();
    if (!email || !addTenantId) {
      setMessage({ type: 'error', text: 'Email and Delivery Unit are required.' });
      return;
    }
    setAdding(true);
    setMessage({ type: '', text: '' });
    try {
      await usersAPI.addDeviceManager({ email, tenantId: addTenantId, role: addRole });
      const roleLabel = addRole === 'device_manager' ? 'Device Manager' : 'Employee';
      setMessage({ type: 'success', text: `${roleLabel} added. They can sign in with Google using that email.` });
      setAddEmail('');
      setAddRole('device_manager');
      setAddUserDialogOpen(false);
      loadDeviceManagers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to add user' });
    } finally {
      setAdding(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user? They will no longer be able to sign in.')) return;
    setMessage({ type: '', text: '' });
    try {
      await usersAPI.deactivateUser(id);
      setMessage({ type: 'success', text: 'User deactivated successfully.' });
      loadDeviceManagers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to deactivate' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently? This action cannot be undone.')) return;
    setMessage({ type: '', text: '' });
    try {
      await usersAPI.deleteUser(id);
      setMessage({ type: 'success', text: 'User deleted successfully.' });
      loadDeviceManagers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete user' });
    }
  };

  const handleEdit = (user) => {
    setEditingUserId(user.id);
    setEditDeliveryUnit(user.deliveryUnit || user.tenantId);
    setEditRole(user.role);
    setMessage({ type: '', text: '' });
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditDeliveryUnit('');
    setEditRole('');
  };

  const handleSaveEdit = async (userId) => {
    if (!editDeliveryUnit) {
      setMessage({ type: 'error', text: 'Please select a delivery unit.' });
      return;
    }
    if (!editRole) {
      setMessage({ type: 'error', text: 'Please select a role.' });
      return;
    }
    
    setUpdating(true);
    setMessage({ type: '', text: '' });
    try {
      await usersAPI.updateDeviceManager(userId, { tenantId: editDeliveryUnit, role: editRole });
      setMessage({ type: 'success', text: 'User updated successfully.' });
      setEditingUserId(null);
      setEditDeliveryUnit('');
      setEditRole('');
      loadDeviceManagers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update user' });
    } finally {
      setUpdating(false);
    }
  };

  // Export users with device allocations to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Fetch devices and assignments
      const [devicesRes, assignmentsRes] = await Promise.all([
        deviceAPI.getAll(),
        assignmentAPI.getAll()
      ]);
      
      const devices = devicesRes.data;
      const assignments = assignmentsRes.data.filter(a => a.status === 'active');
      
      // Create a map of user email to their devices
      const userDevices = {};
      assignments.forEach(assignment => {
        const device = devices.find(d => d.assetId === assignment.deviceId);
        if (device) {
          if (!userDevices[assignment.employeeId]) {
            userDevices[assignment.employeeId] = [];
          }
          userDevices[assignment.employeeId].push({
            assetId: device.assetId,
            model: device.model || 'N/A'
          });
        }
      });

      // Create CSV
      const headers = ['Email', 'Name', 'Role', 'Delivery Unit', 'Status', 'Allocated Device IDs', 'Allocated Device Models'];
      let csvContent = headers.join(',') + '\n';
      
      list.forEach(user => {
        const allocatedDevices = userDevices[user.email] || [];
        const deviceIds = allocatedDevices.map(d => d.assetId).join('; ');
        const deviceModels = allocatedDevices.map(d => d.model).join('; ');
        
        const row = [
          user.email || '',
          user.name || 'Pending login',
          user.role === 'device_manager' ? 'Device Manager' : 'Employee',
          user.deliveryUnit || user.tenantId || '',
          user.isActive ? 'Active' : 'Inactive',
          deviceIds || '',
          deviceModels || ''
        ].map(field => {
          const stringField = String(field);
          if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
            return `"${stringField.replace(/"/g, '""')}"`;
          }
          return stringField;
        });
        
        csvContent += row.join(',') + '\n';
      });

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setMessage({ type: 'success', text: 'Users exported successfully!' });
    } catch (error) {
      console.error('Error exporting users:', error);
      setMessage({ type: 'error', text: 'Failed to export users. Please try again.' });
    } finally {
      setExporting(false);
    }
  };

  // Handle import file selection
  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setMessage({ type: 'error', text: 'Please select a CSV file.' });
        return;
      }
      setImportFile(file);
      setImportErrors([]);
      setImportSuccess(false);
    }
  };

  // Import users from CSV
  const handleImportCSV = async () => {
    if (!importFile) return;
    
    setImporting(true);
    setImportErrors([]);
    setImportSuccess(false);
    
    try {
      const text = await importFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setImportErrors(['CSV file is empty or has no data rows']);
        setImporting(false);
        return;
      }
      
      // Parse header
      const header = lines[0].split(',').map(h => h.trim().toLowerCase());
      const emailIndex = header.findIndex(h => h.includes('email'));
      const nameIndex = header.findIndex(h => h.includes('name'));
      const roleIndex = header.findIndex(h => h.includes('role'));
      const duIndex = header.findIndex(h => h.includes('delivery') || h.includes('unit'));
      
      if (emailIndex === -1 || roleIndex === -1 || duIndex === -1) {
        setImportErrors(['CSV must have Email, Role, and Delivery Unit columns']);
        setImporting(false);
        return;
      }
      
      const errors = [];
      let successCount = 0;
      
      // Process each row
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) continue;
        
        // Simple CSV parsing (handles quoted fields)
        const values = [];
        let current = '';
        let inQuotes = false;
        
        for (let char of line) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());
        
        const email = values[emailIndex]?.trim().toLowerCase();
        const name = values[nameIndex]?.trim() || '';
        const roleValue = values[roleIndex]?.trim().toLowerCase();
        const deliveryUnit = values[duIndex]?.trim();
        
        // Validate
        if (!email || !email.includes('@')) {
          errors.push(`Row ${i + 1}: Invalid email`);
          continue;
        }
        
        if (!roleValue || (roleValue !== 'device_manager' && roleValue !== 'device manager' && roleValue !== 'employee')) {
          errors.push(`Row ${i + 1}: Role must be "Device Manager" or "Employee"`);
          continue;
        }
        
        if (!deliveryUnit) {
          errors.push(`Row ${i + 1}: Delivery Unit is required`);
          continue;
        }
        
        // Find matching tenant
        const tenant = tenants.find(t => 
          t.deliveryUnit === deliveryUnit || 
          t.name === deliveryUnit ||
          t.tenantId === deliveryUnit
        );
        
        if (!tenant) {
          errors.push(`Row ${i + 1}: Delivery Unit "${deliveryUnit}" not found`);
          continue;
        }
        
        const role = roleValue.includes('manager') ? 'device_manager' : 'employee';
        
        try {
          await usersAPI.addDeviceManager({ 
            email, 
            tenantId: tenant.tenantId,
            role 
          });
          successCount++;
        } catch (err) {
          errors.push(`Row ${i + 1}: ${err.response?.data?.error || 'Failed to add user'}`);
        }
      }
      
      if (successCount > 0) {
        setImportSuccess(true);
        setMessage({ type: 'success', text: `Successfully imported ${successCount} user(s)` });
        loadDeviceManagers();
      }
      
      if (errors.length > 0) {
        setImportErrors(errors);
        if (successCount === 0) {
          setMessage({ type: 'error', text: 'Import failed. Please check the errors below.' });
        }
      }
    } catch (error) {
      console.error('Error importing users:', error);
      setImportErrors(['Failed to process CSV file. Please check the format.']);
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportErrors([]);
    setImportSuccess(false);
  };

  const handleDownloadTemplate = () => {
    const template = 'Email,Name,Role,Delivery Unit\nuser@example.com,John Doe,Employee,DU1\nmanager@example.com,Jane Smith,Device Manager,DU2';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'users_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendEmails = async () => {
    let payload = {};
    let confirmMessage = '';

    if (emailMode === 'deliveryUnit') {
      if (!selectedDeliveryUnit) {
        setMessage({ type: 'error', text: 'Please select a Delivery Unit' });
        return;
      }

      const usersInDU = list.filter(u => u.deliveryUnit === selectedDeliveryUnit || u.tenantId === selectedDeliveryUnit);
      if (usersInDU.length === 0) {
        setMessage({ type: 'error', text: 'No users found in this Delivery Unit' });
        return;
      }

      payload = { deliveryUnit: selectedDeliveryUnit };
      confirmMessage = `Send device assignment summary emails to ${usersInDU.length} user(s) in ${selectedDeliveryUnit}?`;
    } else {
      // Specific users mode
      if (selectedUsers.length === 0) {
        setMessage({ type: 'error', text: 'Please select at least one user' });
        return;
      }

      // SAFETY CHECK: Warn if too many users selected
      if (selectedUsers.length > 50) {
        if (!window.confirm(`⚠️ WARNING: You are about to send emails to ${selectedUsers.length} users. This seems like a lot. Did you mean to select all users?\n\nClick OK to proceed or Cancel to review your selection.`)) {
          return;
        }
      }

      payload = { userEmails: selectedUsers };
      confirmMessage = `Send device assignment summary emails to ${selectedUsers.length} selected user(s)?`;
      
      console.log('=== FRONTEND SENDING EMAIL REQUEST ===');
      console.log('Mode: Specific Users');
      console.log('Selected users count:', selectedUsers.length);
      console.log('Selected users:', selectedUsers);
      console.log('Payload:', JSON.stringify(payload, null, 2));
      console.log('======================================');
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSendingEmails(true);
    setEmailResults(null);
    setMessage({ type: '', text: '' });

    try {
      console.log('Sending request to backend with payload:', payload);
      const response = await usersAPI.sendAssignmentEmails(payload);
      console.log('Backend response:', response.data);
      setEmailResults(response.data);
      setMessage({ 
        type: 'success', 
        text: `Emails sent! Success: ${response.data.successCount}, Failed: ${response.data.failureCount}` 
      });
    } catch (error) {
      console.error('Failed to send emails:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to send emails' 
      });
    } finally {
      setSendingEmails(false);
    }
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEmailMode('deliveryUnit');
    setSelectedDeliveryUnit('');
    setSelectedUsers([]);
    setUserSearchTerm('');
    setEmailResults(null);
  };

  const handleToggleUser = (userEmail) => {
    console.log('=== TOGGLE USER ===');
    console.log('Toggling user:', userEmail);
    console.log('Current selectedUsers:', selectedUsers);
    setSelectedUsers(prev => {
      const newSelection = prev.includes(userEmail)
        ? prev.filter(email => email !== userEmail)
        : [...prev, userEmail];
      console.log('New selectedUsers:', newSelection);
      console.log('==================');
      return newSelection;
    });
  };

  const handleSelectAllUsers = () => {
    console.log('=== SELECT ALL USERS ===');
    console.log('Current selection count:', selectedUsers.length);
    console.log('Total users in list:', list.length);
    
    // Filter users based on current search term
    const filteredUsers = list.filter(user => {
      if (!userSearchTerm) return true;
      const searchLower = userSearchTerm.toLowerCase();
      const emailMatch = (user.email || '').toLowerCase().includes(searchLower);
      const nameMatch = (user.name || '').toLowerCase().includes(searchLower);
      return emailMatch || nameMatch;
    });
    
    const filteredEmails = filteredUsers.map(u => u.email);
    const allFilteredSelected = filteredEmails.every(email => selectedUsers.includes(email));
    
    if (allFilteredSelected && filteredEmails.length > 0) {
      // Deselect all filtered users
      console.log('Action: Deselecting', filteredEmails.length, 'filtered users');
      setSelectedUsers(prev => prev.filter(email => !filteredEmails.includes(email)));
    } else {
      // Select all filtered users (add to existing selection)
      console.log('Action: Selecting', filteredEmails.length, 'filtered users');
      setSelectedUsers(prev => {
        const newSelection = [...new Set([...prev, ...filteredEmails])];
        return newSelection;
      });
    }
    console.log('========================');
  };

  // Filter users by email search
  const filteredList = list.filter(user => {
    if (!searchTerm) return true;
    return user.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Calculate statistics by delivery unit
  const getDeliveryUnitStats = () => {
    const stats = {};
    const allDeviceTypes = new Set();
    
    // Initialize stats for each tenant
    tenants.forEach(tenant => {
      stats[tenant.tenantId] = {
        deliveryUnit: tenant.deliveryUnit,
        name: tenant.name,
        userCount: 0,
        deviceTypes: {}
      };
    });

    // Count users per delivery unit
    list.forEach(user => {
      const duId = user.deliveryUnit || user.tenantId;
      if (stats[duId]) {
        stats[duId].userCount++;
      }
    });

    // Count device types per delivery unit
    assignments.forEach(assignment => {
      const device = devices.find(d => d.assetId === assignment.deviceId);
      if (device) {
        const duId = device.deliveryUnit;
        if (stats[duId]) {
          const deviceType = device.deviceType || device.type || 'Unknown';
          allDeviceTypes.add(deviceType);
          stats[duId].deviceTypes[deviceType] = (stats[duId].deviceTypes[deviceType] || 0) + 1;
        }
      }
    });

    return {
      stats: Object.values(stats).filter(stat => stat.userCount > 0 || Object.keys(stat.deviceTypes).length > 0),
      deviceTypes: Array.from(allDeviceTypes).sort()
    };
  };

  const { stats: deliveryUnitStats, deviceTypes: allDeviceTypes } = getDeliveryUnitStats();

  if (loading) {
    return (
      <div className="card">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>User Management</h2>
        
        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '1.5rem',
          gap: '0.5rem'
        }}>
          <button
            onClick={() => setActiveTab('users')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'users' ? '2px solid #667eea' : '2px solid transparent',
              color: activeTab === 'users' ? '#667eea' : '#64748b',
              fontWeight: activeTab === 'users' ? '600' : '400',
              fontSize: '0.9375rem',
              cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            <i className="fas fa-users" style={{ marginRight: '0.5rem' }}></i>
            Users
          </button>
          <button
            onClick={() => setActiveTab('statistics')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'statistics' ? '2px solid #667eea' : '2px solid transparent',
              color: activeTab === 'statistics' ? '#667eea' : '#64748b',
              fontWeight: activeTab === 'statistics' ? '600' : '400',
              fontSize: '0.9375rem',
              cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            <i className="fas fa-chart-bar" style={{ marginRight: '0.5rem' }}></i>
            Delivery Unit Statistics
          </button>
        </div>

        {/* Statistics Tab Content */}
        {activeTab === 'statistics' && deliveryUnitStats.length > 0 && (
          <div className="table-container">
            <table style={{ fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th>Delivery Unit</th>
                  <th>Total Users</th>
                  {allDeviceTypes.map(deviceType => (
                    <th key={deviceType}>{deviceType}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {deliveryUnitStats.map((stat) => (
                  <tr key={stat.deliveryUnit}>
                    <td style={{ fontWeight: '600' }}>
                      {stat.deliveryUnit}
                      {stat.name && (
                        <div style={{ fontSize: '0.8125rem', color: '#64748b', fontWeight: '400', marginTop: '0.25rem' }}>
                          {stat.name}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        backgroundColor: stat.userCount > 0 ? '#667eea' : '#e5e7eb',
                        color: stat.userCount > 0 ? 'white' : '#6b7280',
                        display: 'inline-block'
                      }}>
                        {stat.userCount}
                      </span>
                    </td>
                    {allDeviceTypes.map(deviceType => (
                      <td key={deviceType}>
                        {stat.deviceTypes[deviceType] ? (
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            backgroundColor: '#10b981',
                            color: 'white',
                            display: 'inline-block'
                          }}>
                            {stat.deviceTypes[deviceType]}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Users Tab Content */}
        {activeTab === 'users' && (
          <>
            {message.text && (
              <div className={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
                {message.text}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginBottom: '1rem',
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
              {canManageUsers && (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setEmailDialogOpen(true)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    <span>📧</span>
                    Send Device Summary Emails
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleExportCSV}
                    disabled={exporting}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem'
                    }}
                  >
                    <span>📥</span>
                    {exporting ? 'Exporting...' : 'Export to CSV'}
                  </button>
                </>
              )}
              {isSuperAdmin && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setImportDialogOpen(true)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <span>📤</span>
                  Import from CSV
                </button>
              )}
              {canManageUsers && (
                <button
                  className="btn btn-primary"
                  onClick={() => setAddUserDialogOpen(true)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <i className="fas fa-user-plus"></i>
                  Add User
                </button>
              )}
            </div>

            {/* Search Input */}
            <div style={{ marginBottom: '1.5rem' }}>
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '0.75rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              />
              {searchTerm && (
                <span style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#64748b' }}>
                  Found {filteredList.length} user{filteredList.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Delivery Unit</th>
                  <th>Devices</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
            {filteredList.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '1.5rem' }}>
                  {searchTerm ? `No users found matching "${searchTerm}"` : 'No users yet. Add one above.'}
                </td>
              </tr>
            ) : (
              filteredList.map((dm) => (
                <tr key={dm.id}>
                  <td>{dm.email}</td>
                  <td>{dm.name || 'Pending login'}</td>
                  <td>
                    {editingUserId === dm.id ? (
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        style={{ 
                          padding: '0.375rem 0.5rem',
                          fontSize: '0.875rem',
                          borderRadius: '4px',
                          border: '1px solid #cbd5e0',
                          minWidth: '150px'
                        }}
                        disabled={updating}
                      >
                        <option value="device_manager">Device Manager</option>
                        <option value="employee">Employee</option>
                      </select>
                    ) : (
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: dm.role === 'device_manager' ? '#667eea' : '#10b981',
                        color: 'white'
                      }}>
                        {dm.role === 'device_manager' ? 'Device Manager' : 'Employee'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingUserId === dm.id ? (
                      <select
                        value={editDeliveryUnit}
                        onChange={(e) => setEditDeliveryUnit(e.target.value)}
                        style={{ 
                          padding: '0.375rem 0.5rem',
                          fontSize: '0.875rem',
                          borderRadius: '4px',
                          border: '1px solid #cbd5e0',
                          minWidth: '150px'
                        }}
                        disabled={updating}
                      >
                        {tenants.filter(t => t.isActive).map(t => (
                          <option key={t.tenantId} value={t.tenantId}>
                            {t.deliveryUnit} {t.name && `(${t.name})`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      dm.deliveryUnit || dm.tenantId
                    )}
                  </td>
                  <td>
                    <span style={{ 
                      padding: '0.25rem 0.5rem', 
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      backgroundColor: dm.deviceCount > 0 ? '#3b82f6' : '#e5e7eb',
                      color: dm.deviceCount > 0 ? 'white' : '#6b7280'
                    }}>
                      {dm.deviceCount || 0}
                    </span>
                  </td>
                  <td>{dm.lastLogin ? new Date(dm.lastLogin).toLocaleString() : '—'}</td>
                  <td>{dm.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {editingUserId === dm.id ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.8125rem' }}
                            onClick={() => handleSaveEdit(dm.id)}
                            disabled={updating}
                          >
                            {updating ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.8125rem' }}
                            onClick={handleCancelEdit}
                            disabled={updating}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {dm.isActive && (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary"
                                style={{ padding: '0.25rem 0.75rem', fontSize: '0.8125rem' }}
                                onClick={() => handleEdit(dm)}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ padding: '0.25rem 0.75rem', fontSize: '0.8125rem', backgroundColor: '#f59e0b' }}
                                onClick={() => handleDeactivate(dm.id)}
                              >
                                Deactivate
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger"
                                style={{ padding: '0.25rem 0.75rem', fontSize: '0.8125rem', backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                                onClick={() => handleDelete(dm.id)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
          </>
        )}
      </div>

      {/* Add User Dialog */}
      {addUserDialogOpen && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Add New User</h3>
            
            <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Add users by email and assign them a role and Delivery Unit. They will sign in with Google; their access level depends on their assigned role.
            </p>

            <form onSubmit={handleAdd}>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="dm-email" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                  Email <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  id="dm-email"
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="user@company.com"
                  required
                  style={{ 
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="dm-role" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                  Role <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  id="dm-role"
                  value={addRole}
                  onChange={(e) => setAddRole(e.target.value)}
                  required
                  style={{ 
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                >
                  <option value="device_manager">Device Manager</option>
                  <option value="employee">Employee</option>
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="dm-tenant" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                  Delivery Unit <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  id="dm-tenant"
                  value={addTenantId}
                  onChange={(e) => setAddTenantId(e.target.value)}
                  required
                  style={{ 
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                >
                  {tenants.filter(t => t.isActive).map(t => (
                    <option key={t.tenantId} value={t.tenantId}>
                      {t.deliveryUnit} {t.name && `(${t.name})`}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setAddUserDialogOpen(false);
                    setAddEmail('');
                    setAddRole('device_manager');
                  }}
                  disabled={adding}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={adding}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  {adding ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                      Adding...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-plus" style={{ marginRight: '0.5rem' }}></i>
                      Add User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      {importDialogOpen && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Import Users from CSV</h3>
            
            {/* Template Download */}
            <div style={{ 
              marginBottom: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#f0f9ff', 
              borderRadius: '4px',
              border: '1px solid #bae6fd'
            }}>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600' }}>
                Need a template?
              </p>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#64748b' }}>
                Download our CSV template to ensure the correct format.
              </p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDownloadTemplate}
                style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
              >
                📄 Download CSV Template
              </button>
            </div>

            {/* File Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="csv-file-input" style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                fontSize: '0.875rem'
              }}>
                Select CSV File
              </label>
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                onChange={handleImportFileChange}
                disabled={importing}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              />
              <p style={{ 
                margin: '0.5rem 0 0 0', 
                fontSize: '0.75rem', 
                color: '#64748b' 
              }}>
                CSV must include: Email, Role (Device Manager/Employee), Delivery Unit. Name is optional.
              </p>
            </div>

            {/* Import Button */}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleImportCSV}
              disabled={!importFile || importing}
              style={{ 
                width: '100%', 
                marginBottom: '1rem',
                padding: '0.75rem',
                fontSize: '0.875rem'
              }}
            >
              {importing ? 'Importing...' : 'Import Users'}
            </button>

            {/* Success Message */}
            {importSuccess && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#d1fae5',
                border: '1px solid #6ee7b7',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                <p style={{ 
                  margin: 0, 
                  color: '#065f46', 
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}>
                  ✓ {importSuccess}
                </p>
              </div>
            )}

            {/* Error Messages */}
            {importErrors.length > 0 && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '4px',
                marginBottom: '1rem',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                <p style={{ 
                  margin: '0 0 0.5rem 0', 
                  color: '#991b1b', 
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}>
                  ⚠ Import Errors:
                </p>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: '1.25rem',
                  fontSize: '0.875rem',
                  color: '#991b1b'
                }}>
                  {importErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Close Button */}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseImportDialog}
              disabled={importing}
              style={{ 
                width: '100%',
                padding: '0.75rem',
                fontSize: '0.875rem'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Email Dialog */}
      {emailDialogOpen && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e293b' }}>
              📧 Send Device Assignment Summary Emails
            </h3>
            
            <div style={{ 
              marginBottom: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#f0f9ff', 
              borderRadius: '6px',
              border: '1px solid #bae6fd'
            }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#0c4a6e', lineHeight: '1.5' }}>
                Send an email to users with a summary of their assigned devices including Asset ID, Device Name, Model, OS Version, and Last Updated date.
              </p>
              <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.875rem', color: '#0c4a6e', fontWeight: '600' }}>
                Users will be asked to update the system if any devices don't belong to them.
              </p>
            </div>

            {/* Selection Mode Toggle */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.75rem', 
                fontWeight: '600',
                fontSize: '0.875rem',
                color: '#374151'
              }}>
                Select Recipients
              </label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  padding: '0.75rem 1rem',
                  border: `2px solid ${emailMode === 'deliveryUnit' ? '#667eea' : '#e5e7eb'}`,
                  borderRadius: '6px',
                  backgroundColor: emailMode === 'deliveryUnit' ? '#f0f4ff' : 'white',
                  flex: 1,
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="emailMode"
                    value="deliveryUnit"
                    checked={emailMode === 'deliveryUnit'}
                    onChange={(e) => setEmailMode(e.target.value)}
                    disabled={sendingEmails}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>By Delivery Unit</span>
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  padding: '0.75rem 1rem',
                  border: `2px solid ${emailMode === 'specific' ? '#667eea' : '#e5e7eb'}`,
                  borderRadius: '6px',
                  backgroundColor: emailMode === 'specific' ? '#f0f4ff' : 'white',
                  flex: 1,
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="emailMode"
                    value="specific"
                    checked={emailMode === 'specific'}
                    onChange={(e) => setEmailMode(e.target.value)}
                    disabled={sendingEmails}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Select Specific Users</span>
                </label>
              </div>
            </div>

            {/* Delivery Unit Selection */}
            {emailMode === 'deliveryUnit' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="delivery-unit-select" style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: '#374151'
                }}>
                  Select Delivery Unit
                </label>
                <select
                  id="delivery-unit-select"
                  value={selectedDeliveryUnit}
                  onChange={(e) => setSelectedDeliveryUnit(e.target.value)}
                  disabled={sendingEmails}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">-- Select Delivery Unit --</option>
                  {tenants.filter(t => t.isActive).map(tenant => {
                    const usersCount = list.filter(u => 
                      u.deliveryUnit === tenant.tenantId || u.tenantId === tenant.tenantId
                    ).length;
                    return (
                      <option key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.deliveryUnit} {tenant.name && `(${tenant.name})`} - {usersCount} user{usersCount !== 1 ? 's' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Specific Users Selection */}
            {emailMode === 'specific' && (
              <div style={{ marginBottom: '1.5rem' }}>
                {/* Search Input */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder="🔍 Search by email or name..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    disabled={sendingEmails}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#cbd5e1';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <label style={{ 
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    color: '#374151'
                  }}>
                    Select Users ({selectedUsers.length} selected{userSearchTerm ? (() => {
                      const filtered = list.filter(user => {
                        const searchLower = userSearchTerm.toLowerCase();
                        return (user.email || '').toLowerCase().includes(searchLower) ||
                               (user.name || '').toLowerCase().includes(searchLower);
                      });
                      return ` • ${filtered.length} shown`;
                    })() : ''})
                  </label>
                  <button
                    type="button"
                    onClick={handleSelectAllUsers}
                    disabled={sendingEmails}
                    style={{
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.75rem',
                      background: 'none',
                      border: '1px solid #cbd5e1',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#667eea',
                      fontWeight: '500'
                    }}
                  >
                    {(() => {
                      const filtered = list.filter(user => {
                        if (!userSearchTerm) return true;
                        const searchLower = userSearchTerm.toLowerCase();
                        return (user.email || '').toLowerCase().includes(searchLower) ||
                               (user.name || '').toLowerCase().includes(searchLower);
                      });
                      const filteredEmails = filtered.map(u => u.email);
                      const allSelected = filteredEmails.every(e => selectedUsers.includes(e)) && filteredEmails.length > 0;
                      return allSelected ? (userSearchTerm ? 'Deselect Filtered' : 'Deselect All') : (userSearchTerm ? 'Select Filtered' : 'Select All');
                    })()}
                  </button>
                </div>
                <div style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '0.5rem'
                }}>
                  {list.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.875rem', padding: '1rem' }}>
                      No users available
                    </p>
                  ) : (() => {
                    // Filter users based on search term
                    const filteredUsers = list.filter(user => {
                      if (!userSearchTerm) return true;
                      const searchLower = userSearchTerm.toLowerCase();
                      const emailMatch = (user.email || '').toLowerCase().includes(searchLower);
                      const nameMatch = (user.name || '').toLowerCase().includes(searchLower);
                      return emailMatch || nameMatch;
                    });

                    if (filteredUsers.length === 0) {
                      return (
                        <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.875rem', padding: '1rem' }}>
                          No users found matching "{userSearchTerm}"
                        </p>
                      );
                    }

                    return filteredUsers.map(user => (
                      <label
                        key={user.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          backgroundColor: selectedUsers.includes(user.email) ? '#f0f4ff' : 'transparent',
                          transition: 'background-color 0.2s',
                          fontSize: '0.875rem'
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedUsers.includes(user.email)) {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedUsers.includes(user.email)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.email)}
                          onChange={() => handleToggleUser(user.email)}
                          disabled={sendingEmails}
                          style={{ marginRight: '0.75rem' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#1e293b' }}>
                            {user.name || 'Pending login'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.125rem' }}>
                            {user.email} • {user.deliveryUnit || user.tenantId}
                          </div>
                        </div>
                        {user.deviceCount > 0 && (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: '#3b82f6',
                            color: 'white'
                          }}>
                            {user.deviceCount} device{user.deviceCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </label>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Email Results */}
            {emailResults && (
              <div style={{
                padding: '1rem',
                backgroundColor: emailResults.failureCount > 0 ? '#fef3c7' : '#d1fae5',
                border: `1px solid ${emailResults.failureCount > 0 ? '#fbbf24' : '#86efac'}`,
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <p style={{ 
                  margin: '0 0 0.5rem 0', 
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: emailResults.failureCount > 0 ? '#92400e' : '#065f46'
                }}>
                  {emailResults.failureCount > 0 ? '⚠️' : '✅'} Email Results:
                </p>
                <p style={{ 
                  margin: 0,
                  fontSize: '0.875rem',
                  color: emailResults.failureCount > 0 ? '#78350f' : '#047857'
                }}>
                  Success: {emailResults.successCount} | Failed: {emailResults.failureCount}
                </p>
                {emailResults.results && emailResults.results.some(r => !r.success) && (
                  <details style={{ marginTop: '0.75rem', fontSize: '0.8125rem' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: '600', color: '#92400e' }}>
                      View Failed Emails
                    </summary>
                    <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', color: '#78350f' }}>
                      {emailResults.results.filter(r => !r.success).map((result, idx) => (
                        <li key={idx}>{result.email}: {result.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendEmails}
                disabled={
                  sendingEmails || 
                  (emailMode === 'deliveryUnit' && !selectedDeliveryUnit) ||
                  (emailMode === 'specific' && selectedUsers.length === 0)
                }
                style={{ 
                  flex: 1,
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  opacity: (
                    sendingEmails || 
                    (emailMode === 'deliveryUnit' && !selectedDeliveryUnit) ||
                    (emailMode === 'specific' && selectedUsers.length === 0)
                  ) ? 0.5 : 1
                }}
              >
                {sendingEmails ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <span style={{ marginRight: '0.5rem' }}>📧</span>
                    Send Emails
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseEmailDialog}
                disabled={sendingEmails}
                style={{ 
                  flex: 1,
                  padding: '0.75rem',
                  fontSize: '0.875rem'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManagers;
